import { formatQuantity, pendingMarkLabel, stockLevel, stockMeter } from '@kronos/domain'
import type { InventoryItem } from '../../data/inventory'
import { Badge } from '../primitives'

/**
 * How an item's shelf stands, drawn the same way on the tile, the list row and
 * the item page — the rules are `stockLevel` and `stockMeter` in the domain.
 */

/** The bar under the number: on the shelf against the minimum, or against what is fitted. */
export function StockMeter({ item }: { item: InventoryItem }): JSX.Element {
  const level = stockLevel(item)
  const meter = stockMeter(item)
  const title =
    meter.basis === 'minimum'
      ? `${formatQuantity(item.onShelf, item.unit)} on the shelf · minimum ${formatQuantity(item.safetyStock ?? 0, item.unit)}`
      : `${formatQuantity(item.onShelf, item.unit)} on the shelf · ${formatQuantity(item.fitted, item.unit)} ${item.tracking === 'serialized' ? 'fitted' : 'used'}`
  return (
    <span className={`inv-meter inv-meter--${level}`} title={title} role="img" aria-label={title}>
      <span className="inv-meter__fill" style={{ width: `${Math.round(meter.fraction * 100)}%` }} />
      {meter.mark !== null ? <span className="inv-meter__mark" style={{ left: `${meter.mark * 100}%` }} /> : null}
    </span>
  )
}

/**
 * The states worth a badge, most urgent first: nothing on the shelf, at or
 * under the minimum, units waiting for their mark. Things we build say so
 * instead — an empty shelf of those is a build to plan, not a delivery.
 */
export function StockBadges({ item, max = 3 }: { item: InventoryItem; max?: number }): JSX.Element | null {
  const badges: JSX.Element[] = []
  const level = stockLevel(item)
  if (!item.receivable) badges.push(<Badge key="built">Built here</Badge>)
  if (level === 'out') badges.push(<Badge key="out" tone="critical">None on shelf</Badge>)
  if (level === 'low') badges.push(<Badge key="low" tone="warning">Low stock</Badge>)
  const pending = pendingText(item)
  if (pending) badges.push(<Badge key="mark" tone="info">{pending}</Badge>)
  if (badges.length === 0) return null
  return <span className="inv-badges">{badges.slice(0, max)}</span>
}

/** "3 labels to print", "1 serial to write on" — `pendingMarkLabel`, counted. Null when nothing waits. */
export function pendingText(item: Pick<InventoryItem, 'marking' | 'labelsPending'>): string | null {
  const n = item.labelsPending
  if (n <= 0 || pendingMarkLabel(item.marking) === null) return null
  const one = n === 1
  return item.marking === 'qr_label' ? `${n} label${one ? '' : 's'} to print` : `${n} serial${one ? '' : 's'} to write on`
}

/** "22" big, "on shelf" small, and the unit when it is not "each". */
export function ShelfFigure({ item }: { item: InventoryItem }): JSX.Element {
  const unit = item.unit.trim() === '' || item.unit === 'each' ? '' : item.unit
  const n = formatQuantity(item.onShelf, 'each')
  return (
    <span className={`inv-figure inv-figure--${stockLevel(item)}`}>
      <span className="figure inv-figure__n">{n}</span>
      <span className="inv-figure__unit">{unit ? `${unit} on shelf` : 'on shelf'}</span>
    </span>
  )
}
